import { AxiosRequestConfig, AxiosResponse } from "axios";
import * as gql from "gql-query-builder";
import { getAxiosInstances } from "../../../../utils/AppUtils";
import AxiosActions from "../../AxiosActions";
import { graphQlRequest, IGraphQlEnvelope, unwrapGraphQlResponse } from "../graphql/graphql";

// Gentleman's agreement: the BE accepts free text for typeKey, but the FE always sends this.
export const UI_CUSTOMIZATION_TYPE_KEY = "External Application";

export interface IUiCustomizationRecord {
    id: string | number;
    name: string;
    typeKey: string;
    guid: string;
    instanceId: number;
    configuration: Record<string, unknown>;
    created?: string;
    updated?: string;
}

const RECORD_FIELDS = ["id", "name", "typeKey", "guid", "instanceId", "configuration", "created", "updated"];

// All UiCustomization records for one third-party app (its id is the instanceId).
export const getUiCustomizationsByInstanceId = async (
    instanceId: number,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<IUiCustomizationRecord[]> => {
    const query = gql.query({
        operation: "UiCustomization",
        variables: {
            filter: { value: { instanceId: { equal: instanceId } }, type: "UiCustomizationFilterInput" },
        },
        fields: [{ records: RECORD_FIELDS }, "recordsCount"],
    });
    const res = await graphQlRequest<IGraphQlEnvelope<{ UiCustomization: { records: IUiCustomizationRecord[] } }>>(query, spinner, config);
    return unwrapGraphQlResponse(res).UiCustomization.records;
};

// Public, UNAUTHENTICATED read of a single branding config by its GUID (the `?brandingId=` URL param on
// the Login/Register pages). Routed through the dedicated PUBLIC axios instance, which has NO auth
// interceptors — so no bearer is sent and, critically, a stray 401 can never trigger the global
// refresh/logout cascade (NG-8457). `hideErrors` is on because a missing/unknown GUID legitimately 404s
// (branding simply off) — the caller treats an absent result as "render defaults", never an error. Path
// confirmed with BE (NG-8454): V3 `/public/ui-customization/config?guid=<guid>`.
export const PUBLIC_UI_CUSTOMIZATION_PATH = "/public/ui-customization/config";

export const getPublicUiCustomizationByGuid = (
    guid: string,
    config: AxiosRequestConfig = {},
    spinner = false,
): Promise<AxiosResponse<IUiCustomizationRecord> | undefined> =>
    AxiosActions.get(
        getAxiosInstances().PUBLIC,
        PUBLIC_UI_CUSTOMIZATION_PATH,
        { ...config, params: { ...config.params, guid }, withCredentials: false },
        spinner,
        true,
    );

export interface IUiCustomizationInsert {
    name: string;
    guid: string;
    instanceId: number;
    configuration: Record<string, unknown>;
}

export const insertUiCustomization = async (
    payload: IUiCustomizationInsert,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<IUiCustomizationRecord> => {
    const mutation = gql.mutation({
        operation: "InsertUiCustomizationRecord",
        fields: [{ record: ["id", "name", "guid"] }],
        variables: {
            record: {
                type: "GraphQLInsertUiCustomizationInput",
                value: {
                    name: payload.name,
                    typeKey: UI_CUSTOMIZATION_TYPE_KEY,
                    guid: payload.guid,
                    instanceId: payload.instanceId,
                    configuration: payload.configuration,
                },
            },
        },
    });
    const res = await graphQlRequest<IGraphQlEnvelope<{ InsertUiCustomizationRecord: { record: IUiCustomizationRecord } }>>(mutation, spinner, config);
    return unwrapGraphQlResponse(res).InsertUiCustomizationRecord.record;
};

export const updateUiCustomizationById = async (
    id: string | number,
    changes: Partial<Pick<IUiCustomizationRecord, "name" | "configuration">>,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<IUiCustomizationRecord> => {
    const mutation = gql.mutation({
        operation: "UpdateUiCustomizationRecords",
        fields: [{ records: ["id", "name", "guid"] }],
        variables: {
            rsql: { value: `id==${id}`, type: "String!" },
            maxRecordsToUpdate: { value: 1, type: "Long!" },
            record: { value: changes, type: "GraphQLUpdateUiCustomizationInput" },
        },
    });
    const res = await graphQlRequest<IGraphQlEnvelope<{ UpdateUiCustomizationRecords: { records: IUiCustomizationRecord[] } }>>(mutation, spinner, config);
    return unwrapGraphQlResponse(res).UpdateUiCustomizationRecords.records[0];
};

export const deleteUiCustomizationById = async (id: string | number, config: AxiosRequestConfig = {}, spinner = true): Promise<void> => {
    const mutation = gql.mutation({
        operation: "DeleteUiCustomizationRecords",
        fields: [{ records: ["id"] }],
        variables: {
            rsql: { value: `id==${id}`, type: "String!" },
            maxRecordsToDelete: { value: 1, type: "Long!" },
        },
    });
    const res = await graphQlRequest(mutation, spinner, config);
    unwrapGraphQlResponse(res);
};
